import { websiteEventContext } from '@mts/shared/websiteAnalytics';
import { getApiBaseUrl } from './api.js';
import { COOKIE_CATEGORIES, isConsentGiven } from './cookieConsentState.js';

/**
 * Visitor tracking service for anonymous public-page analytics
 *
 * Events that support core navigation/account workflows are treated as necessary.
 * Engagement and impression analytics require Analytics consent. Unknown event
 * types default to Analytics-gated so new tracking is conservative by default.
 */

const VISITOR_COOKIE = 'mts_visitor_id';
const SESSION_COOKIE = 'mts_session_id';
const SESSION_MAX_AGE_SECONDS = 60 * 30;
const EVENT_NAME_ALIASES = new Map([
  ['listing.card_impression', 'listing.card.impression'],
  ['listing.phone_clicked', 'listing.phone.clicked'],
  ['listing.website_clicked', 'listing.website.clicked'],
  ['listing.offer_clicked', 'listing.offer.clicked'],
  ['listing.directions_clicked', 'listing.directions.clicked'],
  ['listing.social_clicked', 'listing.social.clicked'],
  ['listing.claim_started', 'claim.started'],
  ['offer.redeem_started', 'offer.redeem.started'],
  ['user.consent_changed', 'user.consent.changed'],
]);
const NECESSARY_EVENT_TYPES = new Set([
  'listing.viewed',
  'claim.started',
  'search.performed',
  'user.consent.changed',
]);
const ANALYTICS_EVENT_TYPES = new Set([
  'page.viewed',
  'listing.card.viewed',
  'listing.card.clicked',
  'listing.card.impression',
  'listing.phone.clicked',
  'listing.website.clicked',
  'listing.offer.clicked',
  'listing.directions.clicked',
  'listing.social.clicked',
  'offer.impression',
  'offer.clicked',
  'offer.redeem.started',
  'offer.unlocked',
]);

function normalizeEventName(eventType) {
  const raw = typeof eventType === 'string' ? eventType.trim() : '';
  return EVENT_NAME_ALIASES.get(raw) || raw;
}

function randomId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function readCookie(name) {
  const prefix = `${name}=`;
  const cookies = document.cookie ? document.cookie.split('; ') : [];
  for (const cookie of cookies) {
    if (cookie.startsWith(prefix)) {
      return decodeURIComponent(cookie.slice(prefix.length));
    }
  }
  return null;
}

function writeCookie(name, value, options = {}) {
  const parts = [`${name}=${encodeURIComponent(value)}`, 'Path=/', 'SameSite=Lax'];

  if (options.maxAgeSeconds) {
    parts.push(`Max-Age=${options.maxAgeSeconds}`);
  }

  if (window.location.protocol === 'https:') {
    parts.push('Secure');
  }

  document.cookie = parts.join('; ');
}

export function getVisitorEventConsentCategory(eventType) {
  const eventName = normalizeEventName(eventType);
  if (NECESSARY_EVENT_TYPES.has(eventName)) {
    return COOKIE_CATEGORIES.NECESSARY;
  }

  if (ANALYTICS_EVENT_TYPES.has(eventName)) {
    return COOKIE_CATEGORIES.ANALYTICS;
  }

  return COOKIE_CATEGORIES.ANALYTICS;
}

export function canTrackVisitorEvent(eventType) {
  const category = getVisitorEventConsentCategory(eventType);
  return category === COOKIE_CATEGORIES.NECESSARY || isConsentGiven(category);
}

export function getOrCreateVisitorId() {
  const existing = readCookie(VISITOR_COOKIE);
  if (existing) return existing;

  const created = randomId();
  writeCookie(VISITOR_COOKIE, created, { maxAgeSeconds: 60 * 60 * 24 * 365 });
  return created;
}

export function getOrCreateSessionId() {
  const existing = readCookie(SESSION_COOKIE);
  if (existing) {
    writeCookie(SESSION_COOKIE, existing, { maxAgeSeconds: SESSION_MAX_AGE_SECONDS });
    return existing;
  }

  const created = randomId();
  writeCookie(SESSION_COOKIE, created, { maxAgeSeconds: SESSION_MAX_AGE_SECONDS });
  return created;
}

export function linkVisitorIdentityToCurrentUser() {
  const visitorId = getOrCreateVisitorId();
  const url = `${getApiBaseUrl()}/api/auth/visitor/link`;

  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ visitorId }),
    credentials: 'include',
  })
    .then(async (response) => {
      if (!response.ok) {
        return null;
      }

      return response.json();
    })
    .catch(() => null);
}

function buildPayload({ eventType, listingId = null, metadata = null }) {
  const eventName = normalizeEventName(eventType);
  const context = websiteEventContext({ pageUrl: window.location.href, referrer: document.referrer || null, canonicalOrigin: window.location.origin, analyticsConsent: isConsentGiven(COOKIE_CATEGORIES.ANALYTICS) });
  const safeMetadata = Object.fromEntries(Object.entries(metadata || {}).filter(([key,value]) => ['context','section','index','tier','variant','pathname','targetPath','slug','source','module'].includes(key) && ['string','number','boolean'].includes(typeof value)).map(([key,value]) => [key, typeof value === 'string' ? value.slice(0,200) : value]));
  return {
    visitorId: getOrCreateVisitorId(),
    sessionId: getOrCreateSessionId(),
    eventType: eventName,
    listingId,
    pageUrl: `${window.location.origin}${context?.path || window.location.pathname}`,
    referrer: context?.referrerHost ? `https://${context.referrerHost}/` : null,
    metadata: { ...safeMetadata, websiteAnalytics: { version: 1, analyticsConsent: context?.analyticsConsent === true, taggedCampaign: context?.channel === 'tagged_campaign', paidTag: context?.channel === 'paid_tag' } },
  };
}

export function trackVisitorEvent({ eventType, listingId = null, metadata = null }) {
  if (!canTrackVisitorEvent(eventType)) {
    return Promise.resolve(false);
  }

  if (!websiteEventContext({ pageUrl: window.location.href, canonicalOrigin: window.location.origin })) return Promise.resolve(false);
  const payload = buildPayload({ eventType, listingId, metadata });
  const url = `${getApiBaseUrl()}/api/visitor-events`;
  const body = JSON.stringify(payload);

  // Skip sendBeacon for visitor tracking since it always sends credentials
  // and conflicts with wildcard CORS policy. Use fetch with credentials: omit instead.
  
  return fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body,
    credentials: 'omit', // Don't send cookies for visitor tracking (CORS with wildcard *)
    keepalive: true,
  })
    .then(response => response.ok)
    .catch((error) => {
      // Silently fail - visitor tracking is non-critical
      // Log only in development for debugging
      if (import.meta.env?.DEV) {
        console.debug('Visitor tracking failed (non-critical):', error.message);
      }
      return false;
    });
}

/**
 * Log a consent change event when user modifies their cookie preferences
 * Called when user accepts/rejects cookie categories via consent modal
 * @param {Object} consentState - Current consent state with accepted categories
 * @param {Array} changedCategories - Array of categories that changed
 * @returns {Promise<boolean>} Success status
 */
export function trackConsentChange({ consentState, changedCategories }) {
  const url = `${getApiBaseUrl()}/api/activity/events`;
  return fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    keepalive: true,
    body: JSON.stringify({
      ...buildPayload({ eventType: 'user.consent.changed' }),
      eventName: 'user.consent.changed',
      source: 'web',
      metadata: {
        accepted: consentState,
        changed: changedCategories,
        timestamp: new Date().toISOString(),
      },
    }),
  }).then(() => true).catch(() => false);
}
